import { projectId, publicAnonKey } from '/utils/supabase/info';

export const SUBSCRIPTION_UPDATED_EVENT = 'tension-subscription-updated';

export interface SubscriptionSyncPayload {
  plan: string;
  status: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  billingPeriod?: string;
}

export type SyncSubscriptionFromServerResult =
  | { ok: true; subscription: SubscriptionSyncPayload | null }
  | { ok: false; error: string };

function persistSubscriptionToStorage(sub: SubscriptionSyncPayload | null) {
  if (!sub) return;
  localStorage.setItem('tension_subscription', sub.plan || 'free');
  localStorage.setItem('tension_subscription_data', JSON.stringify(sub));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT));
  }
}

/**
 * Single GET to the Edge Function — same as Settings → Sync.
 */
export async function syncSubscriptionFromServer(
  accessToken: string,
): Promise<SyncSubscriptionFromServerResult> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/sync-subscription`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${publicAnonKey}`,
          'X-User-Token': accessToken,
        },
      },
    );

    const data = (await response.json().catch(() => ({}))) as {
      subscription?: SubscriptionSyncPayload;
      error?: string;
    };

    if (!response.ok) {
      return { ok: false, error: data.error || 'Failed to sync subscription' };
    }

    const subscription = data.subscription ?? null;
    if (subscription) {
      persistSubscriptionToStorage(subscription);
    }

    return { ok: true, subscription };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Network error';
    return { ok: false, error: message };
  }
}

export type SyncAfterPaymentResult =
  | { ok: true; activated: true; subscription: SubscriptionSyncPayload }
  | { ok: true; activated: false; subscription: SubscriptionSyncPayload | null }
  | { ok: false; error: string };

/**
 * After Stripe redirects to /pricing?payment=success, the webhook may lag a few seconds.
 * Poll until plan is not `free` or attempts are exhausted (pack-only checkouts stay free).
 */
export async function syncSubscriptionAfterPayment(
  accessToken: string,
  opts?: { maxAttempts?: number; delayMs?: number },
): Promise<SyncAfterPaymentResult> {
  const maxAttempts = opts?.maxAttempts ?? 8;
  const delayMs = opts?.delayMs ?? 1500;

  let last: SyncSubscriptionFromServerResult | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    last = await syncSubscriptionFromServer(accessToken);
    if (last.ok === false) {
      return { ok: false, error: last.error };
    }

    const sub = last.subscription;
    if (sub && sub.plan && sub.plan !== 'free') {
      return { ok: true, activated: true, subscription: sub };
    }

    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }

  return {
    ok: true,
    activated: false,
    subscription: last?.ok ? last.subscription : null,
  };
}
